/**
 * Module Aliases i18n Plugin
 *
 * This plugin watches for changes in MODULE_ALIASES from the auth store
 * and dynamically updates i18n messages for modules.
 *
 * When a company has custom module aliases configured (e.g., "Clientes" → "Asistentes"),
 * this plugin regenerates all the grammatically correct Spanish text and merges it
 * into the i18n locale messages.
 *
 * Flow:
 * 1. User logs in → auth store receives user data with settings.MODULE_ALIASES
 * 2. This plugin watches the auth store user
 * 3. When MODULE_ALIASES changes, it generates new messages using generateModuleMessages()
 * 4. Messages are merged into i18n using setLocaleMessage()
 */

import { SystemModulesData, SystemSettingKeys } from '@openbox/shared-types'
import type { ModuleAliasInput } from '~/config/grammar.config'
import {
  DEFAULT_CUSTOMER_MESSAGES,
  generateBaseMessages,
  generateCustomerMessages,
} from '~/utils/module-alias-generator'

/**
 * Map of module keys to their generator functions
 * Each module can have its own message generator
 */
const MODULE_GENERATORS: Record<
  string,
  (input: ModuleAliasInput) => Record<string, unknown>
> = {
  customers: generateCustomerMessages,
}

/**
 * Map of module keys to their default messages (fallback when no alias)
 */
const MODULE_DEFAULTS: Record<string, Record<string, unknown>> = {
  customers: DEFAULT_CUSTOMER_MESSAGES,
}

/**
 * Map of module keys to their alias keys in the API response
 * The API returns MODULE_ALIASES as Record<uuid, { s, p, g }>
 * We need to map our module names to those UUIDs or identifiers
 */
const MODULE_ALIAS_KEYS: Record<string, string> = {
  customers: SystemModulesData.Customers.id,
}

export default defineNuxtPlugin(nuxtApp => {
  const authStore = useAuthStore()

  /**
   * Update module messages in i18n based on MODULE_ALIASES
   * If no aliases are configured, resets to default messages
   */
  function updateModuleAliases() {
    const aliases = authStore.getSetting(SystemSettingKeys.MODULE_ALIASES)
    const hasAliases = aliases && Object.keys(aliases).length > 0

    // Access i18n instance from nuxt app
    const i18n = nuxtApp.$i18n as {
      locale: { value: string }
      getLocaleMessage: (locale: string) => Record<string, unknown>
      setLocaleMessage: (
        locale: string,
        messages: Record<string, unknown>
      ) => void
    }

    if (!i18n) return

    const locale = i18n.locale.value
    const currentMessages = i18n.getLocaleMessage(locale)
    const modulesMessages = (currentMessages.modules || {}) as Record<
      string,
      unknown
    >

    // Process each module
    for (const [moduleKey, aliasKey] of Object.entries(MODULE_ALIAS_KEYS)) {
      const aliasInput = hasAliases ? aliases[aliasKey] : undefined

      if (aliasInput) {
        // Generate messages from alias
        const generator = MODULE_GENERATORS[moduleKey] ?? generateBaseMessages
        modulesMessages[moduleKey] = generator(aliasInput)
      } else {
        // Reset to default messages
        const defaultMessages = MODULE_DEFAULTS[moduleKey]
        if (defaultMessages) {
          modulesMessages[moduleKey] = defaultMessages
        }
      }
    }

    // Merge back into locale
    i18n.setLocaleMessage(locale, {
      ...currentMessages,
      modules: modulesMessages,
    })
  }

  // Watch for user changes (login/logout/company switch)
  watch(
    () => authStore.user,
    () => {
      updateModuleAliases()
    },
    { immediate: true }
  )
})
